import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import { useSeo } from '@/hooks/useSeo';
import {
  applyFilters,
  getMediaByType,
  sortMedia,
  typeLabel,
  type MediaFilters,
  type SortKey,
} from '@/utils/media';
import FilterBar from './components/FilterBar';

const PAGE_SIZE = 8;

const emptyFilters: MediaFilters = {
  genres: [],
  regions: [],
  yearFrom: null,
  yearTo: null,
  keyword: '',
};

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const [filters, setFilters] = useState<MediaFilters>(emptyFilters);
  const [sort, setSort] = useState<SortKey>('updated');
  const [page, setPage] = useState(1);

  const typeItems = useMemo(() => getMediaByType(type), [type]);
  const availableGenres = useMemo(() => {
    const set = new Set<string>();
    typeItems.forEach((item) => item.genres.forEach((g) => set.add(g)));
    return Array.from(set);
  }, [typeItems]);

  const result = useMemo(() => {
    return sortMedia(applyFilters(typeItems, filters), sort);
  }, [typeItems, filters, sort]);

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = result.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const label = typeLabel(type);
  const description =
    type === 'movie'
      ? '天堂影视电影频道汇集动作、科幻、喜剧、爱情等各类型院线佳片，支持按地区、年份筛选与多维排序，快速找到你想看的电影。'
      : type === 'tv'
        ? '天堂影视电视剧频道涵盖古装、都市、悬疑、奇幻等热门剧集，支持按地区、年份筛选与排序，轻松追剧不迷路。'
        : '天堂影视影视库汇集最新电影与热播剧集，支持按类型、地区、年份筛选与多维排序，总有一部适合你。';

  useSeo({ title: `${label} - 天堂影视`, description });

  const updateFilters = (next: MediaFilters) => {
    setFilters(next);
    setPage(1);
  };

  const reset = () => {
    setFilters(emptyFilters);
    setSort('updated');
    setPage(1);
  };

  return (
    <div className="container-page py-6">
      <nav className="flex items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">{label}</span>
      </nav>

      <div className="mt-4 rounded-lg bg-primary-950 px-5 py-6 text-background-50 md:px-7 md:py-8">
        <h1 className="text-2xl font-bold md:text-3xl">{label}</h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-background-200/80">
          {description}
        </p>
      </div>

      <div className="mt-5">
        <FilterBar
          detailed={false}
          filters={filters}
          sort={sort}
          availableGenres={availableGenres}
          onFiltersChange={updateFilters}
          onSortChange={(s) => {
            setSort(s);
            setPage(1);
          }}
          onReset={reset}
        />
      </div>

      <div className="mt-5 flex items-center justify-between text-sm text-foreground-500">
        <span>
          共找到 <strong className="text-primary-600">{result.length}</strong> 部{label}
        </span>
        <Link
          to={`/category/${type}/filter`}
          className="flex items-center gap-1 transition-colors hover:text-accent-600 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-filter-3-line"></i>
          高级筛选
        </Link>
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {pageItems.map((item) => (
              <MovieCard key={item.id} item={item} />
            ))}
          </div>
          <Pagination
            current={current}
            totalPages={totalPages}
            onChange={(p) => {
              setPage(p);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </>
      ) : (
        <div className="mt-4">
          <EmptyState
            icon="ri-film-line"
            title="暂无相关内容"
            description="没有找到符合当前筛选条件的影视，试试调整或重置筛选条件。"
            actionLabel="重置筛选"
            onAction={reset}
          />
        </div>
      )}
    </div>
  );
}